import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import CreatorApplicationForm from "../components/CreatorApplicationForm";
import "./CreatorsPage.css";

const galleryItems = [
  {
    src: "/assets/original/images/Screenshot 2025-03-04 at 6.08.04 PM.png",
    width: 2500,
    height: 1616,
    alt: "Air-quality data shown as vertical bars over India on a 3D globe",
    caption: "Air-quality data",
    href: "/stories/feb-2025-bootcamp",
  },
  {
    src: "/assets/original/images/Screenshot10-22@04.42.39.jpg",
    width: 2000,
    height: 1125,
    alt: "A molecular visualization of a CRISPR protein complex interacting with DNA",
    caption: "CRISPR & DNA",
    href: "/crispr",
  },
  {
    src: "/assets/original/images/Lauren Blog.jpg",
    width: 1411,
    height: 753,
    alt: "A model of the Lower San Fernando Dam failure displayed in a virtual museum",
    caption: "Dam-failure modeling",
    href: "/stories/lauren-eliza-talbot",
    className: "creators-hero__figure--dam",
  },
  {
    src: "/assets/creators/bootcamp-ribosomes.jpg",
    width: 840,
    height: 420,
    alt: "A close-up molecular view from the bacterial ribosome project",
    caption: "Bacterial ribosomes",
    href: "/stories/feb-2025-bootcamp",
  },
];

export default function CreatorsPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== "#application" && hash !== "#cohorts") return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "instant" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hash]);

  return (
    <article className="creators-page">
      <section className="creators-hero">
        <div className="content-width creators-hero__grid">
          <div className="creators-hero__copy">
            <p className="eyebrow">Research data visualization</p>
            <h1>10k Science<br />Creator Program</h1>
            <p className="creators-hero__lead">
              A free, fully remote program for scientists interested in using
              interactive 3D visualization to communicate their research.
            </p>
            <p>
              Work with the 10k Science team over a two-week sprint to develop
              a visualization using your own research data. Scientists from
              any country are welcome to apply.
            </p>
            <div className="creators-hero__actions">
              <a className="button-link" href="#application">Apply to join</a>
              <a className="creators-text-link" href="#cohorts">View cohort dates</a>
            </div>
          </div>
          <div className="creators-hero__gallery">
            {galleryItems.map((item) => (
              <figure className={`creators-hero__figure ${item.className || ""}`} key={item.src}>
                <div className="creators-hero__image">
                  <img
                    alt={item.alt}
                    src={item.src}
                    width={item.width}
                    height={item.height}
                    decoding="async"
                  />
                </div>
                <figcaption><Link to={item.href}>{item.caption}</Link></figcaption>
              </figure>
            ))}
          </div>
        </div>
        <dl className="content-width creators-facts">
          <div><dt>Cost</dt><dd>Free to participate</dd></div>
          <div><dt>Format</dt><dd>Fully remote</dd></div>
          <div><dt>Commitment</dt><dd>About 10 hours over two weeks</dd></div>
        </dl>
        <div className="content-width creators-support">
          <img
            alt="National Institutes of Health"
            src="/assets/original/images/NIH_Master_Logo_Vertical_White.png"
            width="375"
            height="330"
          />
          <div>
            <p>
              Development of 10k Science is supported by a Small Business
              Innovation Research (SBIR) grant from the National Institutes
              of Health (NIH).
            </p>
            <Link className="creators-text-link" to="/stories/10k-science-an-nih-funded-platform-for-science-education-launches-a-new-vr-experience-on-uc-davis-research-into-non-opioid-pain-relief">
              About our NIH support
            </Link>
          </div>
        </div>
      </section>

      <section className="creators-data">
        <div className="content-width creators-section-grid">
          <div>
            <p className="eyebrow">The platform</p>
            <h2>What is 10k Creator?</h2>
          </div>
          <div className="creators-section-copy">
            <p className="creators-section-lead">
              10k Creator is our authoring platform for turning research data
              into interactive 3D visualizations. Scientists can bring their
              data into 10k Science and add explanations that help people
              explore and understand the research.
            </p>
            <p>
              Visualizations can be viewed on the web and in VR. The program
              uses the web version, so you can work in your browser. A VR
              headset isn’t required, but you can use one if you have one.
            </p>
            <p>
              During the sprint, the 10k Science team will help you work
              through the data import and visualization process.
            </p>
          </div>
        </div>
        <div className="content-width creators-section-grid creators-data__row">
          <div>
            <p className="eyebrow">Your project</p>
            <h2>Working with your data</h2>
          </div>
          <div className="creators-section-copy">
            <p className="creators-section-lead">
              Each project starts with your research and what you want to
              communicate. You might want to explain a complex structure,
              present a numerical model, or make spatial relationships in your
              data easier to understand.
            </p>
            <p>
              Together, we’ll review your dataset and existing visualization
              workflow, identify what can be brought into 10k Science, and
              decide on a manageable project for the sprint. You’ll consider
              both the representation of the data and the context someone
              needs to interpret it.
            </p>
          </div>
        </div>
        <div className="content-width creators-section-grid creators-data__row">
          <div>
            <p className="eyebrow">After the sprint</p>
            <h2>What you get</h2>
          </div>
          <div className="creators-section-copy">
            <p className="creators-section-lead">
              You’ll receive free lifetime access to 10k Creator and a link
              to an interactive visualization of your research, hosted by
              10k Science.
            </p>
            <p>
              Share the link directly or embed the interactive in your lab
              website, personal site, or other webpages. We handle the
              hosting, so you don’t need to set up your own server.
            </p>
          </div>
        </div>
      </section>

      <section className="creators-format">
        <div className="content-width">
          <div className="creators-section-grid">
            <div>
              <p className="eyebrow">Two-week sprints</p>
              <h2>Program format</h2>
            </div>
            <div className="creators-section-copy">
              <p>
                The program builds on our science communication bootcamps,
                with time for project development, technical support, and
                feedback from the team and other participants.
              </p>
              <p>
                Expect approximately 10 hours of participation over two weeks.
                We’ll work with participants to schedule meetings around their
                availability and time zones.
              </p>
            </div>
          </div>
          <div className="creators-weeks">
            <div>
              <h3><span>01</span> Week one</h3>
              <p>Review your data and project goals, learn the platform, and build an initial visualization.</p>
            </div>
            <div>
              <h3><span>02</span> Week two</h3>
              <p>Refine the visualization and supporting explanations, receive feedback, and share your work with the cohort.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="creators-cohorts" id="cohorts">
        <div className="content-width creators-section-grid">
          <div>
            <p className="eyebrow">2026–2027 cohorts</p>
            <h2>Upcoming sprints</h2>
            <p>Choose November, December, or January when you apply. You can also register your interest in future sessions.</p>
          </div>
          <div>
            <div className="creators-cohort">
              <h3>November</h3>
              <p className="creators-cohort__dates"><time dateTime="2026-11-09">November 9</time>–<time dateTime="2026-11-20">20, 2026</time></p>
              <p>Monday–Friday, over two weeks</p>
              <p className="creators-cohort__meetup">Proposed local meetup: Thursday, November 5</p>
            </div>
            <div className="creators-cohort">
              <h3>December</h3>
              <p className="creators-cohort__dates"><time dateTime="2026-12-07">December 7</time>–<time dateTime="2026-12-18">18, 2026</time></p>
              <p>Monday–Friday, over two weeks</p>
              <p className="creators-cohort__meetup">Proposed local meetup: Thursday, December 3</p>
            </div>
            <div className="creators-cohort">
              <h3>January</h3>
              <p className="creators-cohort__dates"><time dateTime="2027-01-11">January 11</time>–<time dateTime="2027-01-22">22, 2027</time></p>
              <p>Monday–Friday, over two weeks</p>
              <p className="creators-cohort__meetup">Proposed local meetup: Thursday, January 7</p>
            </div>
            <p className="creators-cohorts__note">
              We’re planning an optional in-person kickoff meetup for local
              participants before each sprint. Meet other researchers, discuss
              datasets and project ideas, and ask questions about the platform.
              Meetup details will be shared separately; attending isn’t
              required to take part in the remote program.
            </p>
            <a className="creators-text-link" href="#application">Apply for a sprint <span aria-hidden="true">→</span></a>
          </div>
        </div>
      </section>

      <section className="creators-apply" id="application">
        <div className="content-width creators-section-grid">
          <div className="creators-apply__intro">
            <p className="eyebrow">Application</p>
            <h2>Apply for an upcoming sprint</h2>
            <p>
              Choose a cohort or ask to hear about future sessions, and leave
              your contact details.
              You don’t need a project proposal to apply—we’ll discuss your
              research and visualization goals together before the sprint.
            </p>
            <p>We’ll email you with next steps or updates about future sessions.</p>
          </div>
          <CreatorApplicationForm />
        </div>
      </section>
    </article>
  );
}
